/**
 * ListVariableSelector - Seletor de variáveis disponíveis da lista de campanha
 * 
 * Exibe badges clicáveis com as variáveis disponíveis ({{nome}}, {{variavel1}}, etc.)
 * Ao clicar, insere a variável no cursor do textarea
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Variable, User, Phone, Mail } from 'lucide-react';

// =====================================================
// INTERFACES
// =====================================================

interface ListVariableSelectorProps {
  /** Colunas de variáveis disponíveis na lista (ex: ['variavel1', 'variavel2']) */
  variableColumns: string[];
  /** Callback quando uma variável é clicada para inserção */
  onInsert: (variable: string) => void;
}

// =====================================================
// VARIÁVEIS PADRÃO (sempre disponíveis)
// =====================================================

const DEFAULT_VARIABLES = [
  { key: 'nome', label: 'Nome', icon: User },
  { key: 'telefone', label: 'Telefone', icon: Phone },
  { key: 'email', label: 'Email', icon: Mail },
];

// =====================================================
// COMPONENTE
// =====================================================

export function ListVariableSelector({
  variableColumns,
  onInsert,
}: ListVariableSelectorProps) {
  
  /**
   * Handler para inserir variável
   */
  const handleInsert = (variableKey: string) => {
    onInsert(`{{${variableKey}}}`);
  };

  // Se não tem variáveis da lista, mostra apenas as padrão
  const hasCustomVariables = variableColumns.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Variable className="h-4 w-4" />
        <span>Variáveis disponíveis (clique para inserir)</span>
      </div>

      {/* Variáveis padrão */}
      <div className="flex flex-wrap gap-1.5">
        {DEFAULT_VARIABLES.map(({ key, label, icon: Icon }) => (
          <Button
            key={key}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 gap-1 text-xs hover:bg-primary/10 hover:border-primary"
            onClick={() => handleInsert(key)}
          >
            <Icon className="h-3 w-3" />
            {`{{${key}}}`}
          </Button>
        ))}
      </div>

      {/* Variáveis customizadas da lista */}
      {hasCustomVariables && (
        <>
          <div className="text-xs text-muted-foreground mt-2">
            Variáveis da lista:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {variableColumns.map((varName) => (
              <Button
                key={varName}
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs hover:bg-primary/10 hover:border-primary"
                onClick={() => handleInsert(varName)}
              >
                <Variable className="h-3 w-3 mr-1" />
                {`{{${varName}}}`}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
